<template>
  <v-card>
    <v-toolbar color="white" light flat>
      <v-toolbar-title>{{ $t("calendars") }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon @click="showSearch" v-if="!open_search">
        <v-icon>mdi-magnify</v-icon>
      </v-btn>
      <v-btn icon @click="hideSearch" v-else>
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <v-btn icon @click="showAddCalendar" color="secondary">
        <v-icon>mdi-plus</v-icon>
      </v-btn>
    </v-toolbar>
    <v-text-field
      class="px-4"
      v-model="search"
      v-if="open_search"
      rounded
      outlined
      dense
      clearable
      prepend-inner-icon="mdi-magnify"
      :placeholder="$t('search_calendar')"
    >
    </v-text-field>
    <v-list dense>
      <v-list-item-group
        v-model="calendar_selected"
        active-class="primary--text"
        mandatory
        v-if="calendars.length > 0"
      >
        <v-list-item
          v-for="item in calendars"
          :key="item.name"
          style="max-height:60px;"
          :value="item"
          :ripple="false"
        >
          <template v-slot:default="{ active }">
            <v-list-item-avatar
              color="pink lighten-1"
              class="white--text font-weight-bold justify-center"
            >
              {{ item.events_count }}
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>
                <v-icon v-if="!item.editable" x-small color="grey">
                  mdi-lock-outline
                </v-icon>
                {{ item.name }}
              </v-list-item-title>
              <v-list-item-subtitle v-if="hotel_id === null" class="grey--text">
                {{ item.subscribed_hotels.length }} hoteles
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action v-if="canShowActions(item)">
              <v-menu bottom left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    dark
                    icon
                    v-bind="attrs"
                    v-on="on"
                    small
                    color="grey darken-1"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list dense flat>
                  <v-list-item-group>
                    <v-list-item
                      v-if="isModifiable(item)"
                      @click.stop.prevent="showEditCalendar(item)"
                    >
                      <v-list-item-title class="d-flex">
                        <v-icon small class="mr-2">mdi-pencil-outline</v-icon>
                        {{ $t("modify") }}
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      v-if="isDeletable(item)"
                      @click.stop.prevent="showDeleteCalendar(item)"
                    >
                      <v-list-item-title
                        class="d-flex red--text text--darken-1"
                      >
                        <v-icon small class="mr-2" color="red darken-1"
                          >mdi-trash-can-outline</v-icon
                        >
                        {{ $t("delete") }}
                      </v-list-item-title>
                    </v-list-item>
                  </v-list-item-group>
                </v-list>
              </v-menu>
            </v-list-item-action>
          </template>
        </v-list-item>
      </v-list-item-group>
    </v-list>

    <v-dialog
      v-model="show_edit_calendar"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="400"
    >
      <v-card>
        <v-card-title
          center
          class="headline justify-center py-6"
          v-if="item_to_edit.id"
        >
          {{ $t("edit_calendar") }}
        </v-card-title>
        <v-card-title center class="headline justify-center py-6" v-else>
          {{ $t("new_calendar") }}
        </v-card-title>
        <v-card-text class="text-center justify-center" v-if="item_to_edit">
          <v-form ref="edit_calendar_form" v-model="can_set_calendar">
            <v-row>
              <v-col cols="12" class="py-0">
                <v-text-field
                  v-model="item_to_edit.name"
                  maxlength="50"
                  counter
                  :label="$t('name')"
                  :rules="[v => !!v || $t('required')]"
                  :placeholder="$t('name')"
                  outlined
                  rounded
                  dense
                ></v-text-field>
              </v-col>
              <v-col v-if="isAgency" cols="10" class="py-0 text-left">
                <label>{{ $t("is_editable") }}</label>
                <v-switch v-model="item_to_edit.editable"></v-switch>
              </v-col>
              <v-col cols="2" class="py-0"> </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions class="pt-0 pb-5 justify-center">
          <v-btn
            color="secondary"
            rounded
            depressed
            class="pl-5 pr-5"
            @click="setCalendar()"
            :disabled="!can_set_calendar"
            v-if="item_to_edit.id"
          >
            {{ $t("set_calendar") }}
          </v-btn>
          <v-btn
            v-else
            color="secondary"
            rounded
            depressed
            class="pl-5 pr-5"
            @click="addCalendar()"
            :disabled="!can_set_calendar"
          >
            {{ $t("add_calendar") }}
          </v-btn>
          <v-btn
            color=""
            text
            rounded
            depressed
            class="pl-5 pr-5"
            @click="hideEditCalendar"
          >
            {{ $t("cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DELETE CAlENDAR -->
    <v-dialog
      v-model="show_delete_calendar"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="400"
    >
      <v-card>
        <v-card-title center class="headline justify-center">
          {{ $t("are_you_sure") }}
        </v-card-title>
        <v-card-text
          class="text-center justify-center"
          v-if="item_to_edit"
          v-html="$t('sure_delete_calendar', { calendar: item_to_edit.name })"
        >
        </v-card-text>
        <v-card-actions class="ps-5 pb-5 justify-center">
          <v-btn
            color="red"
            rounded
            depressed
            text
            class="pl-5 pr-5"
            @click="deleteCalendar()"
          >
            {{ $t("delete") }}
          </v-btn>
          <v-btn
            color=""
            text
            rounded
            depressed
            class="pl-5 pr-5"
            @click="hideDeleteCalendar"
          >
            {{ $t("cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- DELETE CALENDAR -->
  </v-card>
</template>

<script>
import * as Utils from "@/utils.js";
import Vue from "vue";
import { mapGetters, mapState } from "vuex";

export default {
  name: "CalendarCardList",
  data: () => ({
    search: "",
    open_search: false,
    show_edit_calendar: false,
    show_delete_calendar: false,
    can_set_calendar: null,
    item_to_edit: {
      id: null,
      name: null,
      editable: true
    }
  }),
  props: {
    hotel_id: {
      type: Number,
      default: null
    }
  },
  computed: {
    calendars() {
      let calendars = this.visible_calendars;
      if (this.open_search && this.search && this.search.length > 0) {
        calendars = calendars.filter(c => {
          return c.name.toLowerCase().includes(this.search.toLowerCase());
        });
      }
      return calendars;
    },
    ready() {
      return this.calendar_year && this.current_hotel && this.current_hotel.id;
    },
    isAgency() {
      return (
        this.current_user && this.current_user.permissions.eventsmanagement
      );
    },
    calendar_year: {
      get() {
        return this.calendar_store.calendar_year;
      },
      set(value) {
        this.$store.dispatch("setCalendarYear", value);
      }
    },
    visible_calendars: {
      get() {
        return this.calendar_store.visible_calendars;
      },
      set(value) {
        this.$store.dispatch("setVisibleCalendars", value);
      }
    },
    calendar_selected: {
      get() {
        return this.calendar_store.calendar_selected;
      },
      set(value) {
        this.$store.dispatch("setCalendarSelected", value);
      }
    },
    ...mapState({
      calendar_store: state => state.calendar
    }),
    ...mapGetters({
      current_user: "current_user",
      current_hotel: "current_hotel",
      getHotelCalendars: "getHotelCalendars"
    })
  },
  methods: {
    clearEditForm() {
      if (this.$refs.edit_calendar_form) {
        this.$refs.edit_calendar_form.resetValidation();
      }
    },
    isModifiable(item) {
      return this.isAgency || (!this.isAgency && item.editable);
    },
    isDeletable(item) {
      return (
        (item.events_count === 0 && this.isAgency) ||
        (item.events_count === 0 && !this.isAgency && item.editable)
      );
    },
    canShowActions(item) {
      return this.isModifiable(item) || this.isDeletable(item);
    },
    showAddCalendar() {
      this.clearEditForm();
      this.show_edit_calendar = true;
      this.item_to_edit = {
        name: null
      };
    },
    showEditCalendar(item) {
      if (!item) {
        return;
      }
      this.clearEditForm();
      this.show_edit_calendar = true;
      this.item_to_edit = JSON.parse(JSON.stringify(item));
    },
    hideEditCalendar() {
      this.show_edit_calendar = false;
      this.item_to_edit = {};
    },
    setCalendar: function() {
      this.$store.dispatch("setSnackbar", {
        title: this.$t("modifying_calendar"),
        color: "secondary"
      });

      this.item_to_edit.editable = this.item_to_edit.editable ? true : false;
      this.$store
        .dispatch("setCalendar", this.item_to_edit)
        .then(() => {
          this.show_edit_calendar = false;
          this.$store.dispatch("setSnackbar", {
            title: this.$t("calendar_modified"),
            color: "accent"
          });
          this.item_to_edit = {};
          this.getCalendars();
        })
        .catch(() => {
          this.show_edit_calendar = false;
          this.item_to_edit = {};
          Utils.showError(this, "calendar_not_modified");
        });
    },
    addCalendar: function() {
      this.$store.dispatch("setSnackbar", {
        title: this.$t("adding_calendar"),
        color: "secondary"
      });
      if (!this.isAgency) {
        this.item_to_edit.editable = true;
      }
      if (this.hotel_id) {
        this.item_to_edit.hotel_id = this.hotel_id;
      }
      this.$store
        .dispatch("setCalendar", this.item_to_edit)
        .then(() => {
          this.show_edit_calendar = false;
          this.$store.dispatch("setSnackbar", {
            title: this.$t("calendar_added"),
            color: "accent"
          });
          this.item_to_edit = {};
          this.getCalendars();
        })
        .catch(() => {
          this.show_edit_calendar = false;
          this.item_to_edit = {};
          Utils.showError(this, "calendar_not_added");
        });
    },
    showDeleteCalendar(calendar) {
      if (!calendar) {
        return;
      }
      this.show_delete_calendar = true;
      this.item_to_edit = calendar;
    },
    hideDeleteCalendar() {
      this.show_delete_calendar = false;
      this.item_to_edit = {};
    },
    deleteCalendar: function() {
      this.$store.dispatch("setSnackbar", {
        title: this.$t("deleting_calendar"),
        color: "secondary"
      });

      this.$store
        .dispatch("deleteCalendar", { id: this.item_to_edit.id })
        .then(() => {
          this.show_delete_calendar = false;
          this.$store.dispatch("setSnackbar", {
            title: this.$t("calendar_deleted"),
            color: "accent"
          });
          this.item_to_edit = {};
          this.calendar_selected = null;
          this.getCalendars();
        })
        .catch(() => {
          this.show_delete_calendar = false;
          this.item_to_edit = {};
          Utils.showError(this, "calendar_not_deleted");
        });
    },
    showSearch() {
      this.open_search = true;
    },
    hideSearch() {
      this.open_search = false;
    },
    getCalendars() {
      this.$store
        .dispatch("getCalendars", {})
        .then(() => {
          if (this.hotel_id) {
            this.visible_calendars = this.getHotelCalendars;
          } else {
            this.visible_calendars = this.calendar_store.calendars;
          }
        })
        .catch(() => {
          Utils.showError(this, "something_went_wrong");
        });
    },
    getHotelEvents() {
      this.$store
        .dispatch("getHotelEvents", {
          hotel_id: this.current_hotel.id,
          year: this.calendar_year
        })
        .catch(() => {
          Utils.showError(this, "something_went_wrong");
        });
    },
    getCalendarEvents() {
      if (!this.calendar_selected || this.calendar_selected.id == null) {
        return;
      }
      this.$store
        .dispatch("getCalendarEvents", {
          year: this.calendar_year,
          calendar_id: this.calendar_selected.id
        })
        .catch(() => {
          Utils.showError(this, "something_went_wrong");
        });
    },
    getHotelYearData() {
      this.getHotelEvents();
      this.getCalendars();
    }
  },
  watch: {
    ready(v) {
      if (v) {
        this.getHotelYearData();
      }
    },
    calendar_year() {
      if (this.ready) {
        this.getHotelYearData();
        this.getCalendarEvents();
      }
    },
    calendar_selected(calendar) {
      if (calendar) {
        this.getCalendarEvents();
      }
    }
  },
  mounted() {
    this.calendar_year = Vue.moment()
      .year()
      .toString();
  }
};
</script>
